import { useEffect, useMemo, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import PrintSessionBanner from "@/components/PrintSessionBanner";
import CartPrintLabel from "@/components/print/CartPrintLabel";
import { usePrintPage } from "@/lib/usePrintPage";
import { ArrowLeft, Printer } from "lucide-react";

/**
 * Bulk-print pagina voor karren-labels uit meerdere orders.
 *
 * Input via query-string:
 *   ?ids=<orderId1>,<orderId2>,...
 *   &location_id=<optional>
 *   &auto_print=1
 *   &print_session=<sessionId>
 *
 * Voor elke meegegeven order:
 *   - Haal de laatste calculation_run op (`GET /calculation-runs/{id}` via summary endpoint of via order)
 *   - Render alle CartPrintLabel-pages onder elkaar — elk gebruikt `.print-page`
 *     met `page-break-after: always` (uit index.css)
 *
 * Skip silently als een order nog geen calc heeft.
 */
export default function PrintLabelsCombined() {
    usePrintPage("zebra-label");
    const [searchParams] = useSearchParams();
    const idsStr = searchParams.get("ids") || "";
    const locationIdsParam = searchParams.get("location_ids") || "";
    const legacyLocationId = searchParams.get("location_id") || "";
    const locationIds = useMemo(() => Array.from(new Set(
        locationIdsParam
            .split(",")
            .map((value) => value.trim())
            .filter(Boolean)
            .concat(legacyLocationId ? [legacyLocationId] : [])
    )), [locationIdsParam, legacyLocationId]);
    const autoPrint = searchParams.get("auto_print") === "1";

    const [items, setItems] = useState(null); // [{order, customer, carts, calc_id}]
    const [err, setErr] = useState("");

    useEffect(() => {
        let cancelled = false;
        const ids = idsStr.split(",").filter(Boolean);
        (async () => {
            if (ids.length === 0) {
                setErr("Geen bestellingen meegegeven.");
                return;
            }
            try {
                const results = [];
                for (const oid of ids) {
                    // 1. order details (geeft last_calculation_id)
                    const o = (await api.get(`/orders/${oid}`)).data;
                    if (o.is_stale) {
                        throw new Error(`Bestelling van ${o.customer?.name || oid} is gewijzigd en moet eerst opnieuw worden berekend.`);
                    }
                    if (!o.last_calculation_id) continue;
                    const calc = (await api.get(`/calculation-runs/${o.last_calculation_id}`)).data;
                    let carts = calc.run.carts.filter((c) => c.layers.length > 0);
                    if (locationIds.length) {
                        const selectedLocations = new Set(locationIds);
                        carts = carts.filter((c) => selectedLocations.has(c.location_id || ""));
                    }
                    if (carts.length === 0) continue;
                    results.push({ order: o, customer: calc.customer, carts });
                }
                if (cancelled) return;
                if (results.length === 0) {
                    setErr("Geen karren gevonden in de geselecteerde bestellingen. Voer eerst een berekening uit.");
                } else {
                    setItems(results);
                }
            } catch (e) {
                if (!cancelled) setErr(formatApiErrorDetail(e.response?.data?.detail ?? e.message ?? "Laden mislukt"));
            }
        })();
        return () => { cancelled = true; };
    }, [idsStr, locationIds]);

    // Auto-trigger window.print() na laden
    useEffect(() => {
        if (!autoPrint || !items) return;
        const t = setTimeout(() => window.print(), 600);
        return () => clearTimeout(t);
    }, [autoPrint, items]);

    if (err) {
        return (
            <div className="p-12 max-w-3xl mx-auto" data-testid="print-labels-error">
                <Link
                    to="/printcentrum"
                    className="text-sm text-ink-500 hover:text-florefy-900 mb-4 inline-flex items-center gap-1"
                >
                    <ArrowLeft className="h-3.5 w-3.5" /> Terug naar Printcentrum
                </Link>
                <div className="bg-destructive/10 border border-destructive text-destructive text-sm p-4 rounded-md">
                    {err}
                </div>
            </div>
        );
    }

    if (!items) return <div className="p-12 text-ink-500" data-testid="print-labels-loading">Karren-labels laden…</div>;

    const totalCarts = items.reduce((s, it) => s + it.carts.length, 0);
    const previewItem = items[0];
    const previewCart = previewItem?.carts?.[0];

    return (
        <>
            <PrintSessionBanner />
            <div className="no-print p-8 lg:p-12 max-w-4xl mx-auto" data-testid="print-labels-preview">
                <PageHeader
                    kicker="Printcentrum"
                    title={`${totalCarts} karren-labels van ${items.length} bestelling${items.length === 1 ? "" : "en"}`}
                    subtitle="Controleer hieronder de eerste sticker. Bij printen worden alle stickers automatisch per bestelling klaargezet."
                    testid="print-labels-header"
                    actions={
                        <button
                            onClick={() => window.print()}
                            data-testid="btn-print-labels-now"
                            className="px-5 py-2.5 rounded-xl bg-florefy-900 hover:bg-florefy-700 text-white text-sm font-semibold flex items-center gap-2 shadow-sm"
                        >
                            <Printer className="h-4 w-4" /> Print nu
                        </button>
                    }
                />
                <div className="bg-white border border-border rounded-2xl divide-y divide-border overflow-hidden shadow-sm">
                    {items.map((it, i) => (
                        <div key={it.order.id} className="px-5 py-3 flex items-center justify-between gap-3 text-sm">
                            <div className="min-w-0">
                                <div className="font-medium text-ink-900 truncate">
                                    {it.customer?.name || "Onbekende klant"}
                                </div>
                                <div className="text-xs text-ink-500 font-mono">
                                    {it.order.delivery_date} · {it.carts.length} karren
                                </div>
                            </div>
                            <div className="font-mono text-[10px] uppercase tracking-widest text-ink-400">
                                #{i + 1}
                            </div>
                        </div>
                    ))}
                </div>

                {previewCart && (
                    <section className="mt-8" aria-labelledby="sticker-preview-title">
                        <div className="mb-4">
                            <div className="text-xs font-semibold uppercase tracking-[0.18em] text-florefy-700">Stickercontrole</div>
                            <h2 id="sticker-preview-title" className="mt-1 text-xl font-semibold text-ink-950">Zo komt de sticker uit de printer</h2>
                            <p className="mt-1 text-sm text-ink-500">Voorbeeld van de eerste kar. De overige stickers gebruiken exact dezelfde opmaak.</p>
                        </div>
                        <div className="rounded-3xl border border-ink-200 bg-ink-100/70 p-5 sm:p-8 flex justify-center overflow-auto">
                            <CartPrintLabel
                                cart={{ ...previewCart, cart_no: 1 }}
                                totalCarts={previewItem.carts.length}
                                order={previewItem.order}
                                customer={previewItem.customer}
                                cartType={previewItem.order?.cart_type || "CC"}
                            />
                        </div>
                    </section>
                )}
            </div>

            {/* Print-only block met alle labels */}
            <div className="print-only">
                {items.flatMap((it) =>
                    it.carts.map((c, idx) => (
                        <CartPrintLabel
                            key={`${it.order.id}-${idx}`}
                            cart={{ ...c, cart_no: idx + 1 }}
                            totalCarts={it.carts.length}
                            order={it.order}
                            customer={it.customer}
                            cartType={it.order?.cart_type || "CC"}
                        />
                    ))
                )}
            </div>
        </>
    );
}
